import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { toast } from 'sonner';

// URL del servicio del lector NFC (puerto 9500 por defecto).
// Override por dispositivo: localStorage 'nfc_bridge_url' (ej. ws://192.168.10.x:9500 si el bridge está en otro PC).
const getBridgeUrl = (): string => {
  if (typeof window !== 'undefined' && window.localStorage) {
    const stored = window.localStorage.getItem('nfc_bridge_url');
    if (stored && stored.trim()) return stored.trim();
  }
  return 'ws://localhost:9500';
};

interface NfcBridgeState {
  isReaderReady: boolean;
  lastRead: { uid: string; timestamp: number } | null;
  isConnecting: boolean;
  isWriting: boolean;
  error: string | null;
}

interface ScannerContextValue {
  nfcBridge: NfcBridgeState;
  reconnect: () => void;
  writeTag: (data: any) => Promise<boolean>;
}

const defaultState: NfcBridgeState = {
  isReaderReady: false,
  lastRead: null,
  isConnecting: false,
  isWriting: false,
  error: null,
};

const ScannerContext = createContext<ScannerContextValue>({
  nfcBridge: defaultState,
  reconnect: () => {},
  writeTag: async () => false,
});

export const useScannerContext = () => useContext(ScannerContext);

interface ScannerProviderProps {
  children: ReactNode;
}

export function ScannerProvider({ children }: ScannerProviderProps) {
  const [state, setState] = useState<NfcBridgeState>(defaultState);
  const [ws, setWs] = useState<WebSocket | null>(null);
  const [reconnectAttempt, setReconnectAttempt] = useState(0);

  const connect = useCallback(() => {
    // No intentar conectar si ya está conectado o conectando
    if (ws?.readyState === WebSocket.OPEN || ws?.readyState === WebSocket.CONNECTING) {
      return;
    }

    setState(prev => ({ ...prev, isConnecting: true, error: null }));

    try {
      const socket = new WebSocket(getBridgeUrl());

      socket.onopen = () => {
        console.log('[NFC Bridge] Conectado al lector NFC');
        setState(prev => ({
          ...prev,
          isReaderReady: true,
          isConnecting: false,
          error: null,
        }));
        setReconnectAttempt(0);
      };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('[NFC Bridge] Mensaje recibido:', data);

          // Handle bridge format: { event: 'tag:read', uid: '...' }
          if (data.event === 'tag:read' && data.uid) {
            console.log('[NFC Bridge] Tag leído:', data.uid);
            setState(prev => ({
              ...prev,
              lastRead: { uid: data.uid, timestamp: Date.now() },
            }));
            toast.success(`Tag NFC detectado: ${data.uid}`, {
              duration: 2000,
            });
          }
          // Handle legacy format: { type: 'nfc_read', uid: '...' }
          else if (data.type === 'nfc_read' && data.uid) {
            console.log('[NFC Bridge] Tag leído (legacy):', data.uid);
            setState(prev => ({
              ...prev,
              lastRead: { uid: data.uid, timestamp: Date.now() },
            }));
            toast.success(`Tag NFC detectado: ${data.uid}`, {
              duration: 2000,
            });
          }
          // Handle status events from bridge
          else if (data.event === 'status') {
            setState(prev => ({
              ...prev,
              isReaderReady: data.readerConnected === true,
            }));
          }
          else if (data.event === 'reader:connected') {
            console.log('[NFC Bridge] Lector conectado:', data.readerName);
            setState(prev => ({
              ...prev,
              isReaderReady: true,
            }));
            toast.success(`Lector NFC conectado: ${data.readerName}`, { duration: 3000 });
          }
          else if (data.event === 'reader:disconnected') {
            console.log('[NFC Bridge] Lector desconectado');
            setState(prev => ({
              ...prev,
              isReaderReady: false,
            }));
            toast.warning('Lector NFC desconectado');
          }
          else if (data.type === 'status') {
            setState(prev => ({
              ...prev,
              isReaderReady: data.connected === true,
            }));
          }
          // Handle write responses
          else if (data.event === 'write:success') {
            console.log('[NFC Bridge] Escritura exitosa:', data.data);
            setState(prev => ({ ...prev, isWriting: false }));
            toast.success('Datos escritos en el chip NFC');
          }
          else if (data.event === 'write:error') {
            console.error('[NFC Bridge] Error de escritura:', data.error);
            setState(prev => ({ ...prev, isWriting: false, error: data.error }));
            toast.error(`Error escribiendo NFC: ${data.error}`);
          }
        } catch (e) {
          console.error('[NFC Bridge] Error parseando mensaje:', e);
        }
      };

      socket.onclose = () => {
        console.log('[NFC Bridge] Conexión cerrada');
        setState(prev => ({
          ...prev,
          isReaderReady: false,
          isConnecting: false,
        }));
        setWs(null);

        // Reintentar conexión después de un delay
        const delay = Math.min(5000 * Math.pow(2, reconnectAttempt), 30000);
        setTimeout(() => {
          setReconnectAttempt(prev => prev + 1);
        }, delay);
      };

      socket.onerror = (error) => {
        console.log('[NFC Bridge] Error de conexión (lector no disponible)');
        setState(prev => ({
          ...prev,
          isReaderReady: false,
          isConnecting: false,
          error: 'Lector NFC no disponible',
        }));
      };

      setWs(socket);
    } catch (error) {
      console.error('[NFC Bridge] Error al conectar:', error);
      setState(prev => ({
        ...prev,
        isConnecting: false,
        error: 'Error al conectar con el lector NFC',
      }));
    }
  }, [ws, reconnectAttempt]);

  // Intentar conectar al montar y cuando cambie reconnectAttempt
  useEffect(() => {
    connect();

    return () => {
      if (ws) {
        ws.close();
      }
    };
  }, [reconnectAttempt]);

  // Cleanup al desmontar
  useEffect(() => {
    return () => {
      if (ws) {
        ws.close();
      }
    };
  }, []);

  const reconnect = useCallback(() => {
    if (ws) {
      ws.close();
    }
    setReconnectAttempt(0);
    connect();
  }, [ws, connect]);

  const writeTag = useCallback(async (data: any): Promise<boolean> => {
    return new Promise((resolve) => {
      if (!ws || ws.readyState !== WebSocket.OPEN) {
        toast.error('Lector USB no conectado');
        resolve(false);
        return;
      }

      if (!state.isReaderReady) {
        toast.error('Acerca un chip NFC al lector');
        resolve(false);
        return;
      }

      setState(prev => ({ ...prev, isWriting: true }));

      const handleMessage = (event: MessageEvent) => {
        try {
          const response = JSON.parse(event.data);
          if (response.event === 'write:success') {
            ws.removeEventListener('message', handleMessage);
            resolve(true);
          } else if (response.event === 'write:error') {
            ws.removeEventListener('message', handleMessage);
            resolve(false);
          }
        } catch (e) {
          // Ignore parse errors for other messages
        }
      };

      ws.addEventListener('message', handleMessage);

      // Timeout after 10 seconds
      setTimeout(() => {
        ws.removeEventListener('message', handleMessage);
        setState(prev => ({ ...prev, isWriting: false }));
        resolve(false);
      }, 10000);

      // Send write command
      ws.send(JSON.stringify({ command: 'write', data }));
      console.log('[NFC Bridge] Enviando comando de escritura:', data);
    });
  }, [ws, state.isReaderReady]);

  return (
    <ScannerContext.Provider value={{ nfcBridge: state, reconnect, writeTag }}>
      {children}
    </ScannerContext.Provider>
  );
}

export { ScannerContext };
